import React, { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { Search, Copy, X, BookOpen, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import DrugEvidencePopover from "./DrugEvidencePopover";
import { inheritDoseUnit, splitFraction, parseAmount, productStrengthLabel, isCombinedProductName, buildResolvedRow } from "../lib/doseCompat"; // MT-11B.7 / B.8.1
import { RxQualityPanel } from "./RxQualityPanel"; // MT-11B.8 data-quality guard

const FREQ = [
  "OD", "BD", "TDS", "QID", "HS", "SOS", "Stat",
  "Q4H", "Q6H", "Q8H", "Q12H",
  "Alt day", "Once weekly", "Twice weekly", "Once monthly",
];
const ROUTES = ["Oral", "IV", "IM", "SC", "Inhalation", "Topical", "Sublingual"];
const TIMING = ["Before food", "After food", "With food", "Empty stomach", "At bedtime", "Morning", "-"];
const DURATION_PRESETS = ["3 days", "5 days", "7 days", "10 days", "15 days", "1 month", "3 months", "Continue"];

/**
 * Convert a duration string into total treatment days.
 * Supports: N day(s), N week(s), N month(s), and bare integers ("30" → 30 days).
 */
export function durationDays(str) {
  const s = String(str || "").trim();
  if (!s) return 0;
  // bare number = days
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  const m = s.match(/(\d+)\s*(day|week|month|yr|year)/i);
  if (!m) return 0;
  const n = parseInt(m[1], 10);
  const u = m[2].toLowerCase();
  if (u.startsWith("day")) return n;
  if (u.startsWith("week")) return n * 7;
  if (u.startsWith("month")) return n * 30;
  if (u.startsWith("yr") || u.startsWith("year")) return n * 365;
  return 0;
}

/**
 * Convert a duration string into total treatment weeks. Uses calendar-month
 * convention (1 month = 4 weeks) so that "Once weekly for 3 months" yields
 * exactly 12 doses (not 13).
 */
export function durationWeeks(str) {
  const s = String(str || "").trim();
  if (!s) return 0;
  if (/^\d+$/.test(s)) return Math.floor(parseInt(s, 10) / 7);
  const m = s.match(/(\d+)\s*(day|week|month|yr|year)/i);
  if (!m) return 0;
  const n = parseInt(m[1], 10);
  const u = m[2].toLowerCase();
  if (u.startsWith("day")) return Math.floor(n / 7);
  if (u.startsWith("week")) return n;
  if (u.startsWith("month")) return n * 4;
  if (u.startsWith("yr") || u.startsWith("year")) return n * 52;
  return 0;
}

/**
 * Convert a duration string into total treatment months (1 month = 4 weeks).
 */
export function durationMonths(str) {
  const s = String(str || "").trim();
  if (!s) return 0;
  const m = s.match(/(\d+)\s*(day|week|month|yr|year)/i);
  if (!m) return 0;
  const n = parseInt(m[1], 10);
  const u = m[2].toLowerCase();
  if (u.startsWith("month")) return n;
  if (u.startsWith("yr") || u.startsWith("year")) return n * 12;
  if (u.startsWith("week")) return Math.floor(n / 4);
  if (u.startsWith("day")) return Math.floor(n / 30);
  return 0;
}

/**
 * Compute total quantity for a medicine given its frequency and duration string.
 * Returns 0 (i.e. blank) when frequency doesn't produce a deterministic count
 * (SOS / Continue / unknown).
 */
export function calcQuantity(freq, duration) {
  const f = String(freq || "").trim();
  if (!f) return 0;
  const days = durationDays(duration);
  const weeks = durationWeeks(duration);
  const months = durationMonths(duration);
  if (f === "SOS") return 0; // as needed – doctor overrides
  if (f === "Stat") return 1;
  if (f === "Once weekly") return weeks;
  if (f === "Twice weekly") return weeks * 2;
  if (f === "Once monthly") return months || Math.ceil(days / 30);
  if (f === "Alt day") return Math.ceil(days / 2);
  const dailyMap = { OD: 1, BD: 2, TDS: 3, QID: 4, HS: 1, Q4H: 6, Q6H: 4, Q8H: 3, Q12H: 2 };
  if (dailyMap[f]) return days * dailyMap[f];
  // 1-0-1 style schedule
  const sch = f.match(/^\s*(\d+)\s*-\s*(\d+)\s*-\s*(\d+)\s*$/);
  if (sch) {
    const per = parseInt(sch[1], 10) + parseInt(sch[2], 10) + parseInt(sch[3], 10);
    return days * per;
  }
  return 0;
}

export default function MedicineRow({ row, index, onChange, onDuplicate, onDelete, egfr, onPickGuard }) {
  const [q, setQ] = useState(row.brand_name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState(() => localStorage.getItem("rx-search-source") || "mylist");
  const t = useRef();
  // Fixed-position dropdown anchor — escapes the scrollable Rx table so the
  // suggestion list is never clipped or overlapped by rows below.
  const anchorRef = useRef(null);
  const [dropRect, setDropRect] = useState(null);
  useEffect(() => {
    if (!open) { setDropRect(null); return; }
    const el = anchorRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setDropRect({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 300) });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => { window.removeEventListener("scroll", measure, true); window.removeEventListener("resize", measure); };
  }, [open, results.length]);

  useEffect(() => { setQ(row.brand_name || ""); }, [row.brand_name]);

  const runSearch = async (val, src) => {
    try {
      const ep = src === "mylist" ? "/mydrugs/search" : "/medicines/search";
      const r = await api.get(`${ep}?q=${encodeURIComponent(val)}`);
      setResults(r.data.results);
      setOpen(true);
    } catch (e) { console.debug("[silent]", e); }
  };

  const doSearch = (val) => {
    setQ(val);
    onChange({ ...row, brand_name: val });
    clearTimeout(t.current);
    if (val.trim().length < 2) { setResults([]); setOpen(false); return; }
    t.current = setTimeout(() => runSearch(val, source), 350);
  };

  const toggleSource = () => {
    const next = source === "mylist" ? "olddb" : "mylist";
    setSource(next);
    localStorage.setItem("rx-search-source", next);
    window.dispatchEvent(new CustomEvent("rx-search-source-changed", { detail: next }));
    if (q.trim().length >= 2) runSearch(q, next);
  };

  // Keep the source pill in sync across all Rx rows
  useEffect(() => {
    const h = (e) => setSource(e.detail);
    window.addEventListener("rx-search-source-changed", h);
    return () => window.removeEventListener("rx-search-source-changed", h);
  }, []);

  const pick = (m) => {
    // MT-11B.8.1: replacing a search-group row (or re-picking a resolved one) REBUILDS the row —
    // nothing from the unresolved group's regimen may survive the product change
    if (row.search_group || isCombinedProductName(row.brand_name)) {
      const next = buildResolvedRow(row, m.brand_name, m);
      onChange(next);
      setQ(m.brand_name);
      setOpen(false);
      onPickGuard?.(next);
      return;
    }
    const freq = m.frequency || row.frequency;
    const qty = calcQuantity(freq, row.duration) || row.quantity || "";
    const next = {
      ...row,
      medicine_id: m.medicine_id,
      brand_name: m.brand_name,
      generic_name: m.generic_name,
      composition: m.composition,
      strength: m.strength,
      dosage_form: m.dosage_form,
      route: m.route || row.route,
      dose: m.standard_dose || row.dose,
      frequency: freq,
      timing: m.timing || row.timing,
      instructions: m.instructions || row.instructions,
      quantity: qty,
    };
    onChange(next);
    setQ(m.brand_name);
    setOpen(false);
    onPickGuard?.(next);
  };

  const cheapestSwap = async () => {
    try {
      const r = await api.post("/mydrugs/cheapest", {
        generic_name: row.generic_name, composition: row.composition,
        strength: row.strength, dosage_form: row.dosage_form,
      });
      const d = r.data;
      if (!d.matched) { toast.info("No equivalent found in My List"); return; }
      if (d.brand === row.brand_name) { toast.info(`Already the cheapest in My List: ${d.brand} · ₹${d.price}`); return; }
      onChange({
        ...row,
        _origBrand: row._origBrand ?? row.brand_name,
        brand_name: d.brand,
        medicine_id: d.medicine_id || row.medicine_id,
        composition: d.composition || row.composition,
        _myChoiceCompany: d.manufacturer || "My List",
        _myChoicePack: d.pack,
        _myChoicePrice: d.price,
      });
      setQ(d.brand);
      toast.success(`Cheapest from My List: ${d.brand} · ${d.pack || ""} · ₹${d.price}`);
    } catch (e) { console.debug("[silent]", e); toast.error("Cheapest lookup failed"); }
  };

  const update = (k, v) => {
    const next = { ...row, [k]: v };
    if (k === "frequency" || k === "duration") {
      const qty = calcQuantity(next.frequency, next.duration);
      if (qty) next.quantity = qty;
    }
    if (k === "dose") {
      // MT-11B.7: remember the dose unit so a numeric override ("12") keeps
      // it ("12 U"); a changed dose invalidates any earlier split confirmation.
      const { unit } = parseAmount(v);
      if (unit) next.dose_unit = unit;
      delete next.split_confirmed;
      delete next.admin_quantity;
    }
    onChange(next);
  };

  // MT-11B.7: numeric-only dose inherits its unit on blur — previous dose
  // unit first, then the product-strength unit; never the reverse.
  const finalizeDose = () => {
    const withUnit = inheritDoseUnit(row.dose, row.strength, row.dose_unit, row.dosage_form);
    if (withUnit !== (row.dose || "")) {
      const { unit } = parseAmount(withUnit);
      onChange({ ...row, dose: withUnit, dose_unit: unit || row.dose_unit });
    }
  };
  const frac = splitFraction(row.dose, row.strength, row.dosage_form, row.brand_name);
  const strengthLabel = productStrengthLabel(row);
  const confirmSplit = () => onChange({ ...row, split_confirmed: true, admin_quantity: frac.label });
  const undoSplit = () => { const next = { ...row }; delete next.split_confirmed; delete next.admin_quantity; onChange(next); };

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/50 align-top" data-testid={`rx-row-${index}`}>
      <td className="p-2 text-center text-xs text-slate-500 tabular w-8 sticky left-0 z-10 bg-white">{index + 1}</td>
      <td className="p-2 relative min-w-[260px] sticky left-8 z-10 bg-white border-r border-slate-100">
        <div className="relative" ref={anchorRef}>
          <Search size={12} className="absolute left-2 top-2.5 text-slate-400" />
          <Input
            data-testid={`rx-medicine-input-${index}`}
            value={q}
            onChange={(e) => doSearch(e.target.value)}
            onFocus={() => q.length >= 2 && setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
            placeholder="Search brand/generic…"
            className="h-8 pl-7 pr-16 text-sm"
          />
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); toggleSource(); }}
            title={source === "mylist" ? "Searching your 27,797-drug list — click for the old database" : "Searching the old database — click for My List"}
            className={`absolute right-1 top-1.5 text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border transition-colors ${source === "mylist" ? "bg-pink-600 text-white border-pink-600" : "bg-slate-100 text-slate-600 border-slate-300"}`}
            data-testid={`rx-source-toggle-${index}`}
          >
            {source === "mylist" ? "My List" : "Old DB"}
          </button>
        </div>
        {open && results.length > 0 && dropRect && (
          <div
            style={{ position: "fixed", top: dropRect.top, left: dropRect.left, width: dropRect.width, zIndex: 60 }}
            className="bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-auto"
          >
            {results.map((m) => (
              <button
                key={m.medicine_id}
                onMouseDown={() => pick(m)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-pink-50 border-b border-slate-100"
                data-testid={`rx-medicine-option-${m.medicine_id}`}
              >
                <div className="font-medium text-slate-900">
                  {m.brand_name} <span className="text-slate-500 font-normal">{m.strength}</span>
                  {(m.pack || m.price != null) && (
                    <span className="text-emerald-700 font-normal"> · {m.pack}{m.price != null ? ` · ₹${m.price}` : ""}</span>
                  )}
                </div>
                <div className="text-slate-500">{m.generic_name} · {m.manufacturer}</div>
              </button>
            ))}
          </div>
        )}
        {row.composition && (
          <div className="text-[10px] text-slate-500 mt-1 leading-tight">{row.composition}</div>
        )}
        {strengthLabel && (
          <div className="mt-1 inline-block text-[10px] font-semibold text-pink-800 bg-pink-50 border border-pink-200 rounded px-1.5 py-0.5" data-testid={`rx-strength-${index}`} title="Product strength (from the medicine master) — not the prescribed dose">{strengthLabel}{row.concentration ? ` · ${row.concentration}` : ""}</div>
        )}
        {!strengthLabel && row.concentration && (
          <div className="mt-1 inline-block text-[10px] font-semibold text-pink-800 bg-pink-50 border border-pink-200 rounded px-1.5 py-0.5" data-testid={`rx-strength-${index}`} title="Insulin concentration">{row.concentration}</div>
        )}
        <RxQualityPanel row={row} index={index} onChange={onChange} />
        {row._myChoiceCompany && (
          <div className="text-[10px] text-slate-500 mt-1 italic" data-testid={`rx-mychoice-company-${index}`} title="Brand picked via My Choice tier">
            {row._myChoiceCompany === "Economical fallback"
              ? "no preferred-company brand · using Economical"
              : `${row._myChoiceCompany} brand`}
            {row._myChoicePrice != null && (
              <span className="text-emerald-700 not-italic font-medium" data-testid={`rx-mychoice-price-${index}`}>
                {" "}· {row._myChoicePack || "from My List"} · ₹{row._myChoicePrice}
              </span>
            )}
          </div>
        )}
      </td>
      <td className="p-2">
        <Input value={row.dose || ""} onChange={(e) => update("dose", e.target.value)} onBlur={finalizeDose} className="h-8 text-sm w-20" data-testid={`rx-dose-${index}`} placeholder={row.dose_unit ? `e.g. 12 ${row.dose_unit}` : (strengthLabel || "dose")} title="Prescribed dose with unit (e.g. 50 mg, 12 U, 1 tab)" />
        {frac && !frac.suitable && (
          <div className="mt-1 text-[10px] text-rose-700 leading-tight max-w-[180px]" data-testid={`rx-split-blocked-${index}`}>
            {row.dose} is {frac.label} of a {row.strength} {row.dosage_form || "tablet"} — {frac.reason}
          </div>
        )}
        {frac && frac.suitable && !row.split_confirmed && (
          <div className="mt-1 text-[10px] text-amber-800 leading-tight max-w-[180px]" data-testid={`rx-split-prompt-${index}`}>
            {row.dose} = {frac.label} of a {row.strength} tablet.
            <button type="button" onClick={confirmSplit} className="ml-1 underline font-semibold" data-testid={`rx-split-confirm-${index}`}>Confirm splitting</button>
          </div>
        )}
        {frac && frac.suitable && row.split_confirmed && (
          <div className="mt-1 text-[10px] text-emerald-700 leading-tight max-w-[180px]" data-testid={`rx-split-confirmed-${index}`}>
            Prints “{frac.label} ({row.dose}) of {row.strength} tablet”
            <button type="button" onClick={undoSplit} className="ml-1 underline" data-testid={`rx-split-undo-${index}`}>undo</button>
          </div>
        )}
      </td>
      <td className="p-2">
        <select value={row.route || "Oral"} onChange={(e) => update("route", e.target.value)} className="h-8 text-sm border border-slate-200 rounded px-1 w-24" data-testid={`rx-route-${index}`}>
          {ROUTES.map((r) => <option key={r}>{r}</option>)}
        </select>
      </td>
      <td className="p-2">
        <select value={row.frequency || ""} onChange={(e) => update("frequency", e.target.value)} className="h-8 text-sm border border-slate-200 rounded px-1 w-28" data-testid={`rx-freq-${index}`}>
          <option value="">—</option>
          {FREQ.map((f) => <option key={f}>{f}</option>)}
          {/* When a push engine ships a verbose frequency like "BD (morning & evening)"
              or "OD (morning)", show it as a preserved custom option so the
              doctor sees exactly what will print. */}
          {row.frequency && !FREQ.includes(row.frequency) && (
            <option value={row.frequency}>{row.frequency}</option>
          )}
        </select>
      </td>
      <td className="p-2">
        <select value={row.timing || ""} onChange={(e) => update("timing", e.target.value)} className="h-8 text-sm border border-slate-200 rounded px-1 w-32" data-testid={`rx-timing-${index}`}>
          <option value="">—</option>
          {TIMING.map((f) => <option key={f}>{f}</option>)}
        </select>
      </td>
      <td className="p-2">
        <Input list={`dur-${index}`} value={row.duration || ""} onChange={(e) => update("duration", e.target.value)} className="h-8 text-sm w-24" data-testid={`rx-duration-${index}`} />
        <datalist id={`dur-${index}`}>{DURATION_PRESETS.map((d) => <option key={d} value={d} />)}</datalist>
      </td>
      <td className="p-2"><Input value={row.quantity || ""} onChange={(e) => update("quantity", e.target.value)} className="h-8 text-sm w-16 tabular" data-testid={`rx-qty-${index}`} /></td>
      <td className="p-2"><Input value={row.instructions || ""} onChange={(e) => update("instructions", e.target.value)} placeholder="Special instructions" className="h-8 text-sm" data-testid={`rx-instructions-${index}`} /></td>
      <td className="p-2">
        <div className="flex gap-1">
          {(row.brand_name || row.generic_name) && (
            <DrugEvidencePopover
              brand={row.brand_name}
              generic={row.generic_name}
              composition={row.composition}
              egfr={egfr}
              testid={`rx-evidence-${index}`}
            />
          )}
          {(row.brand_name || row.generic_name) && (
            <button onClick={cheapestSwap} className="p-1 rounded hover:bg-emerald-50 text-emerald-700" title="Swap to the cheapest equivalent from My List" data-testid={`rx-cheapest-${index}`}><IndianRupee size={14} /></button>
          )}
          <button onClick={onDuplicate} className="p-1 rounded hover:bg-slate-100" title="Duplicate" data-testid={`rx-duplicate-${index}`}><Copy size={14} /></button>
          <button onClick={onDelete} className="p-1 rounded hover:bg-red-50 text-red-600" title="Delete" data-testid={`rx-delete-${index}`}><X size={14} /></button>
        </div>
      </td>
    </tr>
  );
}
